import { useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import type { AudioVariantSummary, CatalogSong, LearningFacts, UserSummary } from "@vocab1600/shared";
import { appVersion, hasAnyRole, privilegedRoles, teachingRoles } from "@vocab1600/shared";
import { api, ApiError } from "./api.js";
import { ModelAdmin, UserAdmin } from "./admin-pages.js";
import { AnalyticsPage } from "./analytics-page.js";
import { SongPage } from "./song-page.js";
import { PlayerBar } from "./player-bar.js";
import { PlayerProvider, toPlayItem, useAudioPlayer } from "./player-context.js";
import { LyricStudio } from "./lyric-studio.js";

export function App() {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.me().then(({ user }) => setUser(user)).catch(() => setUser(null)).finally(() => setLoading(false)); }, []);
  if (loading) return <Splash />;
  if (!user) return <Login onAuthenticated={setUser} />;
  return <Shell user={user} onLogout={() => api.logout().finally(() => setUser(null))} />;
}

function Splash() {
  return <main className="splash"><div className="logo-mark">16</div><p>正在进入音乐学习空间…</p></main>;
}

function Login({ onAuthenticated }: { onAuthenticated: (user: UserSummary) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setSubmitting(true); setError("");
    try { onAuthenticated((await api.login({ email, password })).user); }
    catch (cause) { setError(cause instanceof ApiError && cause.status === 401 ? "账号或密码不正确" : "登录失败，请稍后再试"); }
    finally { setSubmitting(false); }
  }
  return <main className="login-page">
    <section className="login-story">
      <div className="brand"><span className="logo-mark">16</span><b>VOCAB BEAT</b></div>
      <div className="story-copy">
        <span className="eyebrow">50 SONGS · 1600 WORDS</span>
        <h1>让每一次播放，<br />都成为可看见的进步。</h1>
        <p>统一账号、独立学情、双旋律歌曲与精准歌词时间轴，将在 v2 中逐步上线。</p>
      </div>
      <div className="orbit orbit-a" /><div className="orbit orbit-b" />
    </section>
    <section className="login-panel">
      <form onSubmit={submit} className="login-card">
        <span className="eyebrow dark">LEARNING CLOUD</span>
        <h2>欢迎回来</h2><p>使用学校或管理员分配的账号登录。</p>
        <label>邮箱<input autoComplete="username" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" required /></label>
        <label>密码<input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 8 位" required /></label>
        {error && <div className="form-error">{error}</div>}
        <button className="primary-button" disabled={submitting}>{submitting ? "正在登录…" : "登录学习空间"}</button>
        <small>生产环境使用安全 Cookie；不会把登录令牌写入 localStorage。</small>
      </form>
    </section>
  </main>;
}

function Shell({ user, onLogout }: { user: UserSummary; onLogout: () => void }) {
  const canTeach = hasAnyRole(user.roles, teachingRoles);
  const canAdmin = hasAnyRole(user.roles, privilegedRoles);
  return <PlayerProvider><div className="app-shell">
    <aside className="sidebar">
      <div className="brand compact"><span className="logo-mark">16</span><b>VOCAB BEAT</b></div>
      <nav>
        <NavLink to="/overview">⌁ <span>学习总览</span></NavLink>
        <NavLink to="/songs">♫ <span>50 首歌单</span></NavLink>
        {canTeach && <NavLink to="/classes">◎ <span>班级学情</span></NavLink>}
        {canTeach && <NavLink to="/analytics">✦ <span>AI 学情分析</span></NavLink>}
        {canAdmin && <NavLink to="/admin/users">☷ <span>用户管理</span></NavLink>}
        {canAdmin && <NavLink to="/admin/models">⚙ <span>模型管理</span></NavLink>}
        {canAdmin && <NavLink to="/admin">◇ <span>系统状态</span></NavLink>}
      </nav>
      <div className="sidebar-footer"><div className="avatar">{user.displayName.slice(0, 1)}</div><div><b>{user.displayName}</b><small>{user.roles.join(" · ")}</small></div><button onClick={onLogout}>退出</button></div>
    </aside>
    <main className="workspace">
      <Routes>
        <Route path="/overview" element={<Overview user={user} />} />
        <Route path="/songs" element={<SongLibrary />} />
        <Route path="/songs/:songNumber" element={<SongPage user={user} />} />
        <Route path="/studio/:songNumber" element={canTeach ? <LyricStudio /> : <Navigate to="/overview" />} />
        <Route path="/classes" element={canTeach ? <ClassOverview /> : <Navigate to="/overview" />} />
        <Route path="/analytics" element={canTeach ? <AnalyticsPage /> : <Navigate to="/overview" />} />
        <Route path="/admin/users" element={canAdmin ? <UserAdmin currentUser={user} /> : <Navigate to="/overview" />} />
        <Route path="/admin/models" element={canAdmin ? <ModelAdmin /> : <Navigate to="/overview" />} />
        <Route path="/admin" element={canAdmin ? <AdminConsole user={user} /> : <Navigate to="/overview" />} />
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </main>
    <PlayerBar />
  </div></PlayerProvider>;
}

function PageHeader({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return <header className="page-header"><div><span className="eyebrow dark">{eyebrow}</span><h1>{title}</h1><p>{copy}</p></div><div className="version-pill">v{appVersion}</div></header>;
}

function Overview({ user }: { user: UserSummary }) {
  const [facts, setFacts] = useState<LearningFacts | null>(null);
  useEffect(() => { api.analytics().then((data) => setFacts(data.facts)).catch(() => undefined); }, []);
  const t = facts?.totals;
  return <div className="page"><PageHeader eyebrow="STUDENT HOME" title={`${user.displayName}，今天从一首歌开始`} copy="下列指标全部来自真实播放事件，没有数据时如实显示为零。" />
    <section className="hero-card"><div><span>今日建议</span><h2>先听 1 首，再复习 8 个词</h2><p>建立稳定节奏，比一次听很多更重要。</p><NavLink to="/songs" className="primary-button inline">打开歌单</NavLink></div><div className="wave-visual"><i/><i/><i/><i/><i/><i/><i/></div></section>
    <section className="metric-grid">
      <Metric label="累计收听" value={`${t?.listeningMinutes ?? 0} 分钟`} detail="最近 14 天真实播放时长" />
      <Metric label="完整听完" value={String(t?.playCompletions ?? 0)} detail={`共播放 ${t?.playStarts ?? 0} 次`} />
      <Metric label="接触歌曲" value={String(t?.distinctSongs ?? 0)} detail="去重后的歌曲数" />
      <Metric label="完成率" value={t ? `${Math.round(t.completionRate * 100)}%` : "0%"} detail="完成次数 / 播放次数" />
    </section>
    {facts && !facts.dataQuality.hasEvents && (
      <div className="form-notice block">最近 14 天还没有收听记录。打开歌单听一首，这里就会出现真实数据。</div>
    )}
    {hasAnyRole(user.roles, teachingRoles) ? (
      // 路线图与实现说明面向教师和管理员，学生看到只会困惑
      <section className="split-grid">
        <article className="panel"><h3>v2 能力路线</h3><Timeline /></article>
        <article className="panel accent-panel">
          <span className="eyebrow">DATA FIRST</span>
          <h3>AI 只负责解读，不负责编造统计</h3>
          <p>原始播放事件先在数据库中聚合成可复核指标，再将指标快照交给模型生成学习建议。模型被明确约束为只能引用事实包中的数字。</p>
        </article>
      </section>
    ) : (
      <section className="split-grid">
        <article className="panel">
          <h3>怎么用这个平台</h3>
          <ol className="how-to">
            <li>在「50 首歌单」里挑一首，点封面进入歌曲页。</li>
            <li>选择一个旋律版本开始播放，底部控制条随时可以暂停、切歌。</li>
            <li>跟着歌词唱；已校准的歌曲会逐行高亮，点某一行可以跳到那里重听。</li>
            <li>点单词卡片可以看中文意思。</li>
          </ol>
        </article>
        <article className="panel accent-panel">
          <span className="eyebrow">今天的目标</span>
          <h3>先完整听完一首</h3>
          <p>完整听完比零散听很多更有效。上面的数字会随着你的收听自动更新。</p>
        </article>
      </section>
    )}
  </div>;
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) { return <article className="metric-card"><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>; }
function Timeline() { return <ol className="timeline"><li className="done"><b>001</b><span>账号、数据库、后台骨架</span></li><li><b>002</b><span>云端音频与多旋律版本</span></li><li><b>003</b><span>精准歌词校准工作台</span></li><li className="done"><b>004</b><span>用户管理、模型管理与 AI 学情分析</span></li><li><b>005</b><span>精准歌词时间轴</span></li></ol>; }

function SongLibrary() {
  const songs = window.APP_SONGS ?? [];
  const [query, setQuery] = useState("");
  const player = useAudioPlayer();
  const filtered = useMemo(() => songs.filter((song) => `${song.num}${song.title}${song.unitName}`.toLowerCase().includes(query.trim().toLowerCase())), [songs, query]);
  return <div className="page"><PageHeader eyebrow="CATALOG" title="50 首主题歌曲" copy="每首歌可试听云端多旋律版本；未通过质检的版本会自动隐藏，待修复后重新上架。" />
    <div className="toolbar"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索编号、歌名或主题" /><span>{filtered.length} 首</span></div>
    <section className="song-grid">{filtered.map((song) => <SongCard key={song.num} song={song} player={player} />)}</section>
  </div>;
}

type Player = ReturnType<typeof useAudioPlayer>;

function SongCard({ song, player }: { song: CatalogSong; player: Player }) {
  const navigate = useNavigate();
  const [variants, setVariants] = useState<AudioVariantSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  async function ensureVariants() {
    if (variants || loading) return;
    setLoading(true);
    try { setVariants((await api.songVariants(song.num)).variants); }
    catch { setVariants([]); }
    finally { setLoading(false); }
  }
  return <article className="song-card" style={{ "--song-hue": song.hue } as React.CSSProperties}>
    <div className="song-cover" role="button" onClick={() => navigate(`/songs/${song.num}`)}>
      <span>{song.unitIcon}</span><b>{song.num}</b><i />
      <em className="cover-hint">查看歌词</em>
    </div>
    <div>
      <small>{song.unitName}</small>
      <h3><button className="link-title" onClick={() => navigate(`/songs/${song.num}`)}>{song.title}</button></h3>
      <p>{song.words.length} 词 · {song.sections.length} 段 · {song.bpm ?? "—"} BPM</p>
      <div className="variant-preview" onMouseEnter={ensureVariants} onFocus={ensureVariants}>
        {variants === null && <button className="chip ghost" onClick={ensureVariants}>{loading ? "加载中…" : "试听"}</button>}
        {variants !== null && variants.length === 0 && <em>音频未上传</em>}
        {variants?.map((variant) => {
          const isActive = player.isCurrent(variant.variantKey) && player.state.playing;
          return <button key={variant.variantKey} className={`chip${isActive ? " active" : ""}`} onClick={() => player.toggle(toPlayItem(song, variant))}>{isActive ? "❚❚" : "▶"} {variant.variantCode}</button>;
        })}
      </div>
    </div>
  </article>;
}

function ClassOverview() {
  const [data, setData] = useState<{ studentCount: number; teacherCount: number; facts: LearningFacts } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { api.teacherOverview().then(setData).catch(() => setError("读取班级数据失败")); }, []);
  const t = data?.facts.totals;
  return <div className="page">
    <PageHeader eyebrow="TEACHER" title="班级学情" copy="所有数字来自真实播放事件表。需要文字解读时，请使用左侧的「AI 学情分析」。" />
    {error && <div className="form-error block">{error}</div>}
    <section className="metric-grid">
      <Metric label="学生账号" value={String(data?.studentCount ?? 0)} detail="启用状态的学生" />
      <Metric label="活跃学习者" value={String(t?.activeLearners ?? 0)} detail="最近 14 天有收听记录" />
      <Metric label="累计收听" value={`${t?.listeningMinutes ?? 0} 分钟`} detail="全班合计" />
      <Metric label="完成率" value={t ? `${Math.round(t.completionRate * 100)}%` : "0%"} detail="完成次数 / 播放次数" />
    </section>
    {data && !data.facts.dataQuality.hasEvents ? (
      <section className="empty-panel">
        <div>◎</div>
        <h3>最近 14 天没有学习记录</h3>
        <p>
          {data.studentCount === 0
            ? "当前还没有学生账号。请先到「用户管理」创建学生账号并分配密码，学生登录收听后，这里会自动出现活跃度、完成率、薄弱歌曲与旋律版本偏好。"
            : "已有学生账号，但还没有人开始收听。学生登录并播放歌曲后，这里会自动出现数据。"}
        </p>
      </section>
    ) : (
      <>
        {data && data.facts.quietLearners.length > 0 && (
          <section className="panel">
            <h3>需要关注的学生</h3>
            <p className="muted small">按最近 14 天收听时长升序排列，用于发现掉队的学生。</p>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>姓名</th><th className="right">收听分钟</th><th>最近活跃</th></tr></thead>
                <tbody>{data.facts.quietLearners.map((s) => (
                  <tr key={s.userId}>
                    <td>{s.displayName}</td>
                    <td className="right">{s.minutes}</td>
                    <td className="mono">{s.lastActiveAt ? new Date(s.lastActiveAt).toLocaleString("zh-CN") : "从未"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        )}
        {data && data.facts.topSongs.length > 0 && (
          <section className="panel">
            <h3>播放最多的歌曲</h3>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>编号</th><th>歌名</th><th className="right">播放</th><th className="right">完成</th></tr></thead>
                <tbody>{data.facts.topSongs.map((song) => (
                  <tr key={song.songNumber}><td className="mono">{song.songNumber}</td><td>{song.title}</td><td className="right">{song.plays}</td><td className="right">{song.completions}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        )}
      </>
    )}
  </div>;
}

function AdminConsole({ user }: { user: UserSummary }) {
  const [system, setSystem] = useState<Record<string, unknown>>({});
  const [userCount, setUserCount] = useState<number | null>(null);
  useEffect(() => {
    api.users().then((data) => setUserCount(data.users.length)).catch(() => undefined);
    if (user.roles.includes("super_admin")) api.system().then((data) => setSystem(data.capabilities)).catch(() => undefined);
  }, [user.roles]);
  return <div className="page">
    <PageHeader eyebrow="ADMIN" title="系统状态" copy="账号与模型的日常维护请使用左侧的「用户管理」与「模型管理」。" />
    <section className="admin-grid">
      <article className="panel">
        <div className="panel-title"><div><span className="eyebrow dark">SHORTCUTS</span><h3>常用入口</h3></div></div>
        <div className="capability-list">
          <div><span>用户管理（{userCount ?? "…"} 个账号）</span><NavLink to="/admin/users" className="link-button">前往</NavLink></div>
          <div><span>模型管理与连通性测试</span><NavLink to="/admin/models" className="link-button">前往</NavLink></div>
          <div><span>AI 学情分析</span><NavLink to="/analytics" className="link-button">前往</NavLink></div>
        </div>
      </article>
      <article className="panel">
        <div className="panel-title"><div><span className="eyebrow dark">CAPABILITIES</span><h3>模块状态</h3></div></div>
        <div className="capability-list">
          {Object.entries(system).map(([key, value]) => <div key={key}><span>{key}</span><b>{String(value)}</b></div>)}
          {!Object.keys(system).length && <p className="muted">仅超级管理员可读取系统能力状态。</p>}
        </div>
      </article>
    </section>
  </div>;
}
